/** @jsxImportSource @emotion/react */
import { css, keyframes } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import ReactSlider from "react-slider";

import { Breakpoint, ColorPalette } from "../utils/style";

export const isDefined = (value) => {
  return value !== undefined && value !== null;
};

export const formatNumber = (value, suffix = "") => {
  // https://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript
  const THOUSAND_SPLIT_REGEX = /\B(?=(\d{3})+(?!\d))/g;
  return value.toString().replace(THOUSAND_SPLIT_REGEX, " ");
};

const height = 4;
const thumbSize = 32;

const FormSliderWithInput = ({ id, min, label, max, step, field, fieldState, suffix = "", values }) => {
  const [popoverStatus, setPopoverStatus] = useState({ isHovered: false, isDragged: false });
  const [isMinMaxValueReached, setIsMinMaxValueReached] = useState(false);
  const [width, setWidth] = useState(0);
  const ref = useRef(null);
  const sliderRef = useRef(null);
  const valueWrapperRef = useRef(null);

  const calculateSliderValue = () => {
    if (field.value) {
      const index = values.indexOf(field.value || 0);

      if (index > -1) return index;

      const closest = values.reduce((prev, curr) =>
        Math.abs(curr - Number(field.value)) < Math.abs(prev - Number(field.value)) ? curr : prev
      );

      return values.indexOf(closest);
    }
    return values.indexOf(field.value || 0);
  };

  const steps = values ? Object.keys(values).map(Number) : undefined;
  const minValue = steps ? 0 : min;
  const maxValue = steps ? steps.length - 1 : max;
  const sliderValue = steps ? calculateSliderValue() : field.value || 0;
  const sliderValueToMax = useRef((sliderValue / (maxValue || 1)) * 100);

  useEffect(() => {
    const roundSliderWidth = () => {
      if (!ref.current || !sliderRef.current) {
        return;
      }

      // When measuring where to position thumb, library uses integer values, but when rendering track, it uses decimal values.
      // So sometimes on edge values track looks longer, even when max value is selected.
      // Hack to fix partial pixel problem. Measure container width, floor round it and set as slider max width
      sliderRef.current.style.maxWidth = `${Math.floor(ref.current.getBoundingClientRect().width)}px`;
    };

    roundSliderWidth();

    window.addEventListener("resize", roundSliderWidth);

    return () => {
      window.removeEventListener("resize", roundSliderWidth);
    };
  }, []);

  const getValue = (withSuffix = true) => {
    const value = field.value; //(values && values[field.value]) ?? field.value; // > max ? max : field.value < min ? min : field.value;

    if (value === null || value === "") {
      return "";
    }

    if (withSuffix && suffix) {
      return formatNumber(value, ` ${suffix}`);
    }

    return formatNumber(value);
  };

  useEffect(() => {
    if (valueWrapperRef.current) {
      setWidth(valueWrapperRef.current.getBoundingClientRect().width);
    } else {
      setWidth(0);
    }
  }, [field.value]);

  useEffect(() => {
    setWidth(101.89);
  }, []);

  const formatedValue = getValue();

  return (
    <div css={styles.container} ref={ref}>
      <div css={styles.inputAndSliderTitleContainer}>
        <div css={styles.inputWrapper(width)}>
          <label css={styles.label} htmlFor={id}>
            {label}
          </label>
          <input
            css={[
              styles.input({
                isDragged: popoverStatus.isDragged,
              }),
            ]}
            id={id}
            inputMode="numeric"
            type="text"
            value={formatedValue}
            onChange={(e) => {
              const newValue = e.target.value.replace(/\D/g, "");
              if (newValue.length === 16) return;

              /*if (steps) {
                const index = values.indexOf(Number(newValue));
                const closest = values.reduce((prev, curr) =>
                  Math.abs(curr - Number(newValue)) < Math.abs(prev - Number(newValue)) ? curr : prev,
                );

                field.onChange(isDefined(newValue) ? (index > -1 ? index : values.indexOf(closest)) : null);
                return;
              }*/
              field.onChange(newValue === "" ? null : Number(newValue));
            }}
            onBlur={(e) => {
              //const newValue = field.value > max ? max : field.value < min ? min : field.value;
              //const roundedValue = Math.round(newValue / 100) * 100;
              const roundedValue = values.reduce((prev, curr) =>
                Math.abs(curr - field.value) < Math.abs(prev - field.value) ? curr : prev
              );
              /*if (steps) {
                const newValue = e.target.value.replace(/\D/g, '');
                const index = values.indexOf(Number(newValue));
                const closest = values.reduce((prev, curr) =>
                  Math.abs(curr - Number(newValue)) < Math.abs(prev - Number(newValue)) ? curr : prev,
                );

                field.onChange(isDefined(newValue) ? (index > -1 ? index : values.indexOf(closest)) : null);
                return;
              }*/
              field.onChange(roundedValue === "" ? null : Number(roundedValue));
            }}
            //readOnly={!!values}
          />
          {formatedValue && (
            <>
              <div className="suffix-wrapper">{suffix}</div>
              <span ref={valueWrapperRef} className="value-wrapper">
                {formatedValue}
              </span>
            </>
          )}
        </div>
      </div>
      <div ref={sliderRef}>
        <ReactSlider
          value={sliderValue}
          onChange={(newValue) => {
            setIsMinMaxValueReached(newValue == minValue || newValue == maxValue);
            if (steps) {
              field.onChange(isDefined(newValue) ? values[newValue] : null);
              return;
            }

            field.onChange(newValue);
          }}
          min={minValue}
          max={maxValue}
          step={steps ? 1 : step}
          css={styles.slider}
          renderTrack={(props, state) =>
            state.index === 0 ? <div {...props} css={styles.track(sliderValueToMax.current)} /> : null
          }
          renderThumb={(props) => (
            <div
              {...props}
              css={styles.thumbContainer(sliderValueToMax.current)}
              aria-label="Slider adjusts your loan amount"
            >
              <div css={sliderValue === minValue && isMinMaxValueReached ? styles.minValueGradient : undefined} />
              <div css={sliderValue === maxValue && isMinMaxValueReached ? styles.maxValueGradient : undefined} />
              <button
                aria-label="Choose a loan amount"
                type="button"
                css={[styles.buttonReset, styles.thumb]}
                onMouseEnter={() => setPopoverStatus({ ...popoverStatus, isHovered: true })}
                onMouseLeave={() => setPopoverStatus({ ...popoverStatus, isHovered: false })}
                onMouseDown={() => setPopoverStatus({ ...popoverStatus, isDragged: true })}
                onMouseUp={() => setPopoverStatus({ ...popoverStatus, isDragged: false })}
                onTouchStart={() => setPopoverStatus({ ...popoverStatus, isDragged: true })}
                onTouchEnd={() => setPopoverStatus({ ...popoverStatus, isDragged: false })}
              >
                <img src="/assets/icons/icon-thumb-grab.svg" alt="" width={14} height={16} />
              </button>
            </div>
          )}
          onBeforeChange={() => {
            document.body.style.cursor = "grabbing";
          }}
          onAfterChange={() => {
            document.body.style.cursor = "auto";
            setPopoverStatus({ isDragged: false, isHovered: false });
          }}
        />
      </div>

      <div css={styles.minMaxValuesContainer}>
        <div>
          <span>
            {values && steps ? values[steps[0]] : formatNumber(minValue || 0)} {suffix}
          </span>
        </div>
        <div>
          <span>
            {/*{values && steps ? values[steps[steps.length - 1]] : formatNumber(max || 0)} {suffix}*/}
            {formatNumber(max || 0)} {suffix}
          </span>
        </div>
      </div>
    </div>
  );
};

export default FormSliderWithInput;

function animateSlider(sliderValueToMax) {
  return keyframes`
    from {
      left: 0;
    }
    to {
      left: calc(${sliderValueToMax}% - ${thumbSize}px);
    }`;
}

function animateThumb(sliderValueToMax) {
  return keyframes`
    from {
      right: 100%;
    }
    to {
      right: calc(100% - ${sliderValueToMax}%);
    }`;
}
const fadeInAnimation = keyframes`
  from {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  to {
    opacity: 0;
  }`;

const fadeValueColor = keyframes`
  from {
    color: ${ColorPalette.grey_200};
  }
  50% {
    color: ${ColorPalette.red_100};
  }
  to {
    color: ${ColorPalette.grey_200};
  }`;

const styles = {
  container: css`
    flex: 1;
  `,
  slider: css`
    background: #cde3eb;
    height: ${height}px;
    position: relative;
    border-radius: 50px;
  `,
  sliderValueOpacity: css`
    opacity: 1;
  `,
  thumbContainer: (sliderValuePercentToMax) => css`
    position: relative;
    top: 50%;
    transform: translate(0, -50%);
    outline: none;
    height: ${thumbSize}px;
    display: flex;
    justify-content: center;
    align-items: center;
    animation-name: ${animateSlider(sliderValuePercentToMax)};
    animation-duration: 1.5s;
    //animation-fill-mode: forwards;
    animation-timing-function: linear;
  `,
  thumb: css`
    background-color: #fff;
    height: ${thumbSize}px;
    width: ${thumbSize}px;
    border-radius: ${thumbSize}px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.05), 0px 0px 2px rgba(0, 0, 0, 0.13), 0px 0px 6px rgba(0, 0, 0, 0.1);

    &:hover {
      cursor: grab;
    }
    &:active {
      cursor: grabbing;
    }
    ${Breakpoint.mobile} {
    }
  `,
  track: (sliderValuePercentToMax) => css`
    background-color: #257fff;
    height: ${height}px;
    border-radius: ${height / 2}px;
    animation-name: ${animateThumb(sliderValuePercentToMax)};
    animation-duration: 1.5s;
    //animation-fill-mode: forwards;
    animation-timing-function: linear;
  `,
  input: ({ isDragged }) => css`
    width: 100%;
    appearance: none;
    outline: none;
    height: 80px;
    border: 1px solid #dcdcdc;
    border-radius: 8px;
    padding: 30px 20px 14px;
    font-family: "Poppins", sans-serif;
    font-weight: 600;
    font-size: 32px;
    line-height: 36px;
    color: #2b333e;

    ${isDragged &&
    css`
      color: ${ColorPalette.blue_200};
      font-weight: bold;
    `}

    :read-only {
      border: none;
      padding-right: 0;
      line-height: 22px;
    }

    :hover {
      cursor: inherit;
    }

    ${Breakpoint.mobile} {
      font-size: 24px;
      padding: 30px 20px 12px;
      height: 68px;
      line-height: 28px;
    }
  `,
  inputAndSliderTitleContainer: css`
    margin-bottom: 38px;
    position: relative;

    ${Breakpoint.mobile} {
      margin-bottom: 22px;
      input {
        border-radius: 8px;
      }
    }
  `,
  minMaxValuesContainer: css`
    font-family: "Open Sans", sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #3a3a3a;
    display: flex;
    justify-content: space-between;
    margin-top: 12px;

    ${Breakpoint.mobile} {
      font-size: 12px;
      line-height: 14px;
    }
  `,
  minMaxValueText: css`
    z-index: 0;
    animation: ${fadeValueColor} 1s;
  `,
  minValueGradient: css`
    z-index: -1;
    overflow: visible;
    width: 60px;
    height: 60px;
    opacity: 0;
    position: absolute;
    left: 0;
    text-align: left;
    background: radial-gradient(circle at 12% 54%, ${ColorPalette.red_100} 0%, transparent 60%);
    animation: ${fadeInAnimation} 1s;
  `,
  maxValueGradient: css`
    z-index: -1;
    overflow: visible;
    width: 60px;
    height: 60px;
    opacity: 0;
    position: absolute;
    right: 0;
    text-align: right;
    background: radial-gradient(circle at 88% 54%, ${ColorPalette.red_100} 0%, transparent 60%);
    animation: ${fadeInAnimation} 1s;
  `,
  buttonReset: css`
    border: none;
    background-color: unset;
    padding: 0;
    cursor: pointer;
  `,
  label: css`
    position: absolute;
    top: 14px;
    left: 20px;
    font-weight: 500;
    font-size: 10px;
    line-height: 12px;
    color: #24272f;
  `,
  inputWrapper: (valueLength) => css`
    position: relative;

    .value-wrapper {
      opacity: 0;
      position: fixed;
      left: -9999px;
      font-family: "Poppins", sans-serif;
      font-weight: 600;
      font-size: 32px;
      line-height: 36px;
    }
    .suffix-wrapper {
      font-family: "Poppins", sans-serif;
      font-weight: 600;
      font-size: 32px;
      line-height: 36px;
      color: #2b333e;
      position: absolute;
      top: 30px;
      left: calc(${valueLength}px + 29px);
    }
    ${Breakpoint.mobile} {
      .suffix-wrapper {
        top: 29px;
        left: calc(${valueLength * 0.75}px + 29px);
        font-size: 24px;
        line-height: 28px;
      }
    }
  `,
};
